
import React from 'react';
import { useSleepSchedule } from './SleepScheduleContext';

interface SleepScheduleSummaryProps {
  date: Date;
}

const SleepScheduleSummary: React.FC<SleepScheduleSummaryProps> = ({ date }) => {
  const { totalNapTime, wakeWindowsOnTime, bedtimeConsistency } = useSleepSchedule();
  
  // Format minutes to hours and minutes
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };
  
  const getScheduleTip = () => {
    // Generate a tip based on the schedule adherence
    const napDifference = totalNapTime.actual - totalNapTime.scheduled;
    
    if (wakeWindowsOnTime.completed < wakeWindowsOnTime.total) {
      return "Try to keep wake windows consistent for better sleep quality.";
    } else if (napDifference < -30) { // More than 30 minutes short on naps
      return "Your child may need a bit more nap time to avoid overtiredness.";
    } else if (napDifference > 30) { // More than 30 minutes extra nap time
      return "Try shortening naps slightly to maintain bedtime consistency.";
    } else if (bedtimeConsistency.onTime < bedtimeConsistency.total) {
      return "Consistent bedtimes help establish better sleep patterns.";
    } else {
      return "Great job following the schedule today!";
    }
  };

  return (
    <div className="mt-8">
      <div className="bg-purple-50 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total nap time */}
          <div className="flex flex-col">
            <span className="text-xl font-semibold text-gray-700">Total nap time</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl text-gray-900">
                {formatDuration(totalNapTime.actual)} / {formatDuration(totalNapTime.scheduled)}
              </span>
            </div>
          </div>
          
          {/* Wake windows */}
          <div className="flex flex-col">
            <span className="text-xl font-semibold text-gray-700">Wake windows</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl text-gray-900">
                {wakeWindowsOnTime.completed} / {wakeWindowsOnTime.total} on time
              </span>
            </div>
          </div>
          
          {/* Bedtime consistency */}
          <div className="flex flex-col">
            <span className="text-xl font-semibold text-gray-700">Bedtime</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl text-gray-900">
                {bedtimeConsistency.onTime} / {bedtimeConsistency.total} on time
              </span>
            </div>
          </div>
        </div>
        
        {/* Tip section */}
        <div className="mt-4 pt-4 border-t border-purple-100">
          <p className="text-purple-700 italic">💡 {getScheduleTip()}</p>
        </div>
      </div>
    </div>
  );
};

export default SleepScheduleSummary;
